/**
 * Contract workspace (operator order 2026-08-29): one contract, six tabs, no
 * noise. Overview · Scope · Compliance risks · Delegated functions · Service
 * levels · Documents. Mounted from the Contracts list and from the vendor's
 * Contracts tab through the same ContractWorkspace export.
 */
import React, { useCallback, useEffect, useState } from 'react';
import ContractReview from './ContractReview';
import SlaRegister from './SlaRegister';
import ScopeTab, { StatePill } from './ScopeTab';
import DocumentsTab from './DocumentsTab';
import { PROVISION_LABELS } from './ContractManager';

const API = process.env.REACT_APP_API_URL;
const TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'scope', label: 'Scope' },
  { id: 'risks', label: 'Compliance risks' },
  { id: 'functions', label: 'Delegated functions' },
  { id: 'slas', label: 'Service levels' },
  { id: 'documents', label: 'Documents' },
];
const ROLE_LABEL = { contract: 'Contract', baa: 'BAA', amendment: 'Amendment', addendum: 'Addendum', exhibit: 'Exhibit' };
const TYPE_LABEL = {
  claims_tpa: 'Claims / TPA', utilization_management: 'Utilization management', pbm: 'Pharmacy benefit manager', credentialing: 'Credentialing',
  marketing_sales: 'Marketing / sales', enrollment: 'Enrollment', customer_service: 'Customer service', provider_primary_care: 'Primary care provider',
  provider_specialty: 'Specialty provider', hospital_facility: 'Hospital / facility', baa: 'Business associate', other: 'Other',
};
const PROGRAM_LABEL = { medicare_advantage: 'Medicare Advantage', part_d: 'Part D', medicaid: 'Medicaid', dual_dsnp: 'D-SNP', unknown: 'Not stated' };
const POSTURE_LABEL = { compliant: 'No material gaps found', minor_deficiencies: 'Minor gaps', material_gaps: 'Material gaps', not_assessable: 'Not assessable' };
// A DATE column arrives as 'YYYY-MM-DD…'; read it as a calendar day, never as UTC midnight localized (the day-early bug).
const fmt = (d) => {
  if (!d) return '—';
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(d));
  const dt = m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : new Date(d);
  return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};
const daysLeft = (d) => (d ? Math.ceil((new Date(d) - Date.now()) / 86400000) : null);

function Card({ title, action, children, className = '' }) {
  return (
    <div className={`bg-white rounded-2xl border border-slate-200/60 shadow-sm ${className}`}>
      <div className="px-4 pt-3 pb-2 flex items-center justify-between">
        <h4 className="text-[12px] font-bold uppercase tracking-wide text-slate-500">{title}</h4>
        {action}
      </div>
      <div className="px-4 pb-4">{children}</div>
    </div>
  );
}
function Fact({ label, value }) {
  return (
    <div>
      <div className="text-[10.5px] uppercase tracking-wide text-slate-400">{label}</div>
      <div className="text-[13px] text-slate-800 font-medium">{value ?? '—'}</div>
    </div>
  );
}
function Stat({ label, value, tone = 'slate' }) {
  const tones = { slate: 'text-slate-800', green: 'text-emerald-700', red: 'text-rose-700', amber: 'text-amber-700' };
  return (
    <div className="rounded-xl bg-slate-50 px-3 py-2">
      <div className={`text-xl font-bold ${tones[tone] || tones.slate}`}>{value ?? 0}</div>
      <div className="text-[11px] text-slate-500">{label}</div>
    </div>
  );
}

function OverviewTab({ detail, scope, review, findings, slaCount, go }) {
  const c = detail.contract || {};
  const counts = scope?.counts || {};
  const docs = scope?.documents || [];
  const left = daysLeft(c.expiration_date);
  const suggestions = (findings || []).filter((f) => !f.verified && f.status !== 'present_adequate' && f.status !== 'not_applicable').length;
  const topScope = (scope?.items || []).filter((i) => i.status === 'active' && i.state !== 'not_addressed').slice(0, 6);
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <Card title="Agreement">
        <div className="grid grid-cols-2 gap-3">
          <Fact label="Vendor" value={scope?.contract?.entity_name || c.entity_name} />
          <Fact label="Contract" value={[c.contract_number, c.title].filter(Boolean).join(' · ') || '—'} />
          <Fact label="Effective" value={fmt(c.effective_date)} />
          <Fact label="Expires" value={c.expiration_date ? <>{fmt(c.expiration_date)}{left !== null && <span className={`ml-1 text-[11px] ${left < 0 ? 'text-rose-600' : left < 90 ? 'text-amber-600' : 'text-slate-400'}`}>({left < 0 ? `${-left} days ago` : `${left} days`})</span>}</> : '—'} />
          <Fact label="Status" value={<span className="capitalize">{String(c.status || '').replace(/_/g, ' ') || '—'}</span>} />
          <Fact label="Delegated for" value={c.description || c.title || '—'} />
        </div>
        {review?.status === 'completed' && (
          <div className="mt-3 pt-3 border-t border-slate-100 grid grid-cols-3 gap-3">
            <Fact label="Type" value={TYPE_LABEL[review.detected_contract_type] || review.detected_contract_type || '—'} />
            <Fact label="Program" value={PROGRAM_LABEL[review.detected_program] || review.detected_program || '—'} />
            <Fact label="State" value={review.detected_state || '—'} />
          </div>
        )}
      </Card>

      <Card title="Scope at a glance" action={<button type="button" onClick={() => go('scope')} className="text-[11.5px] font-semibold text-orange-700">Open scope →</button>}>
        <div className="grid grid-cols-4 gap-2">
          <Stat label="In scope" value={counts.in_scope} tone="green" />
          <Stat label="Out of scope" value={counts.out_of_scope} tone="red" />
          <Stat label="Not addressed" value={counts.not_addressed} />
          <Stat label="Documents read" value={docs.filter((d) => d.scope_read_status === 'completed').length} />
        </div>
        {topScope.length > 0 ? (
          <ul className="mt-3 space-y-1">
            {topScope.map((i) => (
              <li key={i.id} className="flex items-center gap-2 text-[12.5px] text-slate-700">
                <StatePill state={i.state} /><span className="truncate">{i.label}</span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="mt-3 text-[12px] text-slate-400">Nothing read into the register yet.</p>
        )}
      </Card>

      <Card title="Compliance risks" action={<button type="button" onClick={() => go('risks')} className="text-[11.5px] font-semibold text-orange-700">Open review →</button>}>
        {review?.status === 'completed' ? (
          <div className="grid grid-cols-3 gap-2">
            <Stat label="Suggestions" value={suggestions} tone={suggestions ? 'amber' : 'green'} />
            <Stat label="Provisions read" value={(findings || []).length} />
            <div className="rounded-xl bg-slate-50 px-3 py-2">
              <div className="text-[13px] font-bold text-slate-800">{POSTURE_LABEL[review.compliance_posture] || '—'}</div>
              <div className="text-[11px] text-slate-500">Reviewed {fmt(review.completed_at)}</div>
            </div>
          </div>
        ) : review && ['queued', 'classifying', 'reviewing', 'verifying'].includes(review.status) ? (
          <p className="text-[12px] text-sky-700">Review in progress · {review.progress_pct || 0}%</p>
        ) : (
          <p className="text-[12px] text-slate-400">No review run yet.</p>
        )}
      </Card>

      <Card title="Documents" action={<button type="button" onClick={() => go('documents')} className="text-[11.5px] font-semibold text-orange-700">Manage →</button>}>
        {docs.length ? (
          <ul className="space-y-1.5">
            {docs.map((d) => (
              <li key={d.id} className="flex items-center gap-2 text-[12.5px]">
                <span className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 text-[10.5px] font-bold uppercase">{ROLE_LABEL[d.doc_role] || 'Doc'}</span>
                <span className="truncate text-slate-700" title={d.file_name}>{d.file_name}</span>
                <span className="ml-auto text-[11px] text-slate-400 whitespace-nowrap">{d.page_count ? `${d.page_count} pp · ` : ''}{d.scope_read_status === 'completed' ? 'read' : d.scope_read_status === 'failed' ? 'read failed' : d.scope_read_status ? 'reading' : 'not read'}</span>
              </li>
            ))}
          </ul>
        ) : <p className="text-[12px] text-slate-400">No documents on file.</p>}
        <div className="mt-3 pt-3 border-t border-slate-100 grid grid-cols-2 gap-2">
          <button type="button" onClick={() => go('functions')} className="text-left rounded-xl bg-slate-50 px-3 py-2 hover:bg-slate-100">
            <div className="text-xl font-bold text-slate-800">{detail.delegated_functions?.length || 0}</div>
            <div className="text-[11px] text-slate-500">Delegated functions</div>
          </button>
          <button type="button" onClick={() => go('slas')} className="text-left rounded-xl bg-slate-50 px-3 py-2 hover:bg-slate-100">
            <div className="text-xl font-bold text-slate-800">{slaCount ?? '—'}</div>
            <div className="text-[11px] text-slate-500">Service levels</div>
          </button>
        </div>
      </Card>
    </div>
  );
}

function FunctionsTab({ detail, onAddFunction }) {
  const c = detail.contract;
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <Card title="Delegated functions" action={
        <div className="flex items-center gap-3">
          {detail.delegated_functions?.length > 0 && (
            <a href={`${API}/api/fdr/contracts/${c.id}/delegated-functions.pdf`} className="text-[11.5px] font-semibold text-slate-500 hover:text-slate-800"
              title="PDF naming every delegated function with the agreement's own grant language and the federal framework">Export PDF</a>
          )}
          {onAddFunction && <button type="button" onClick={onAddFunction} className="text-[11.5px] font-semibold text-orange-700">+ Add function</button>}
        </div>
      }>
        {(c.description || c.title || c.entity_accreditations) && (
          <div className="text-xs bg-slate-50 border border-slate-100 rounded-lg px-3 py-2 mb-2 text-slate-600">
            {(c.description || c.title) && <div><span className="font-semibold text-slate-700">Delegated for:</span> {c.description || c.title}</div>}
            {c.entity_accreditations && <div className="mt-0.5"><span className="font-semibold text-slate-700">Accreditation:</span> {c.entity_accreditations}</div>}
          </div>
        )}
        {detail.delegated_functions?.length > 0 ? (
          <div className="space-y-1">
            {detail.delegated_functions.map((f) => (
              <div key={f.id} className="text-xs bg-white rounded-lg px-3 py-2 border border-slate-100">
                <div className="flex items-center justify-between">
                  <span className="font-medium text-slate-700 capitalize">{String(f.function_category || '').replace(/_/g, ' ')}</span>
                  <span className="text-slate-400 capitalize">{f.monitoring_frequency?.replace(/_/g, ' ')} · {f.monitoring_method?.replace(/_/g, ' ')}</span>
                </div>
                {f.function_description && f.function_description !== f.function_category && <div className="text-slate-500 mt-0.5">{f.function_description}</div>}
                {f.performance_standards && <div className="text-slate-400 mt-0.5">Standard: {f.performance_standards}</div>}
                {f.evidence_quote && <div className="text-slate-400 mt-0.5 italic">“{f.evidence_quote}”</div>}
              </div>
            ))}
          </div>
        ) : <p className="text-xs text-slate-400">No delegated functions on file. The monitoring calendar and work plan build from these.</p>}
      </Card>
      <Card title="CMS Chapter 21 provisions (self-attested)">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">
          {PROVISION_LABELS.map((p) => (
            <div key={p.key} className="flex items-center gap-1.5 text-xs">
              <span className={`w-3.5 h-3.5 rounded-full flex items-center justify-center text-[9px] font-bold ${c[p.key] ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-600'}`}>{c[p.key] ? '✓' : '×'}</span>
              <span className={c[p.key] ? 'text-slate-700' : 'text-slate-400'}>{p.label}</span>
            </div>
          ))}
        </div>
        <div className="mt-3 pt-3 border-t border-slate-100 text-xs text-slate-500">
          Checklist: <span className={`font-bold ${detail.provisions_summary?.compliance_pct >= 85 ? 'text-emerald-600' : detail.provisions_summary?.compliance_pct >= 60 ? 'text-amber-600' : 'text-red-600'}`}>{detail.provisions_summary?.compliance_pct || 0}%</span>
          <span className="text-slate-400"> · entered by hand at intake; the Compliance risks tab is the grounded reading of the agreement</span>
        </div>
      </Card>
    </div>
  );
}

export function ContractWorkspace({ detail, onAddFunction }) {
  const contractId = detail?.contract?.id || detail?.id;
  const [tab, setTab] = useState('overview');
  const [scope, setScope] = useState(null);
  const [review, setReview] = useState(null);
  const [findings, setFindings] = useState([]);
  const [slaCount, setSlaCount] = useState(null);

  const loadScope = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/fdr/contracts/${contractId}/scope`, { credentials: 'include' });
      const d = await r.json();
      if (d.success) setScope(d);
    } catch {}
  }, [contractId]);
  const loadReview = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/fdr/contracts/${contractId}/review`, { credentials: 'include' });
      const d = await r.json();
      if (d.success) { setReview(d.review); setFindings(d.findings || []); }
    } catch {}
  }, [contractId]);
  const loadSlas = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/fdr/slas?contract_id=${contractId}`, { credentials: 'include' });
      const d = await r.json();
      if (d.success) setSlaCount((d.groups || []).reduce((n, g) => n + (g.slas || []).length, 0));
    } catch {}
  }, [contractId]);

  useEffect(() => { loadScope(); loadReview(); loadSlas(); }, [loadScope, loadReview, loadSlas]);

  // Poll while a document is being read into the register.
  const reading = (scope?.runs || []).some((r) => ['queued', 'reading'].includes(r.status))
    || (scope?.documents || []).some((d) => ['pending', 'extracting'].includes(d.extraction_status) || d.scope_read_status === 'queued');
  useEffect(() => {
    if (!reading) return;
    const id = setInterval(loadScope, 4000);
    return () => clearInterval(id);
  }, [reading, loadScope]);

  const counts = scope?.counts || {};
  const badge = {
    scope: (counts.in_scope || 0) + (counts.out_of_scope || 0) ? { n: (counts.in_scope || 0) + (counts.out_of_scope || 0), cls: 'bg-slate-100 text-slate-600' } : null,
    documents: scope?.documents?.length ? { n: scope.documents.length, cls: 'bg-slate-100 text-slate-600' } : null,
    functions: detail.delegated_functions?.length ? { n: detail.delegated_functions.length, cls: 'bg-slate-100 text-slate-600' } : null,
    slas: slaCount ? { n: slaCount, cls: 'bg-slate-100 text-slate-600' } : null,
  };

  return (
    <div>
      <div className="border-b border-slate-200 overflow-x-auto scrollbar-hide mb-4">
        <nav className="flex gap-1 min-w-max">
          {TABS.map((t) => (
            <button key={t.id} type="button" onClick={() => setTab(t.id)}
              className={`px-3.5 py-2 text-[13px] font-semibold border-b-2 -mb-px whitespace-nowrap ${tab === t.id ? 'border-orange-500 text-orange-700' : 'border-transparent text-slate-500 hover:border-slate-300 hover:text-slate-700'}`}>
              {t.label}
              {badge[t.id] && <span className={`ml-1.5 px-1.5 py-0.5 rounded-full text-[10px] font-bold ${badge[t.id].cls}`}>{badge[t.id].n}</span>}
            </button>
          ))}
        </nav>
      </div>
      {tab === 'overview' && <OverviewTab detail={detail} scope={scope} review={review} findings={findings} slaCount={slaCount} go={setTab} />}
      {tab === 'scope' && <ScopeTab contractId={contractId} scope={scope} reload={loadScope} onGoDocuments={() => setTab('documents')} />}
      {tab === 'risks' && <ContractReview contractId={contractId} documentsManaged />}
      {tab === 'functions' && <FunctionsTab detail={detail} onAddFunction={onAddFunction} />}
      {tab === 'slas' && <SlaRegister contractId={contractId} embedded />}
      {tab === 'documents' && <DocumentsTab contractId={contractId} documents={scope?.documents || []} runs={scope?.runs || []} reload={async () => { await loadScope(); await loadReview(); }} />}
    </div>
  );
}

export default ContractWorkspace;
